<script setup lang="ts">
import { computed, ref } from "vue";
import { retryEndpointHistory, useEndpointHistory, useSettled } from "../composables/useHistory";
import { historyRange, RANGE_LABELS } from "../composables/useRange";
import { formatAvailability, formatGrouped, formatMs, MISSING } from "../format";
import { bucketAxis, fillAxis } from "../series";
import type { EndpointSnapshot } from "../types/api";
import AppIcon from "./AppIcon.vue";
import RangeTabs from "./RangeTabs.vue";
import TimeChart, { type ChartSeries } from "./TimeChart.vue";

const props = defineProps<{ endpoint: EndpointSnapshot }>();

/** The stages of one request, in the order they happen. */
const PHASES = [
  { key: "dns_ms", label: "DNS", hint: "解析域名", color: "var(--phase-1)" },
  { key: "connect_ms", label: "TCP", hint: "建立连接", color: "var(--phase-2)" },
  { key: "tls_ms", label: "TLS", hint: "加密握手", color: "var(--phase-3)" },
  { key: "server_ms", label: "服务器", hint: "等待首字节", color: "var(--phase-4)" }
] as const;

const view = ref<"total" | "phases">("total");
const entry = useEndpointHistory(() => props.endpoint.id, historyRange);
/** What is on screen: the newest loaded history, which lags the selected range while it loads. */
const history = useSettled(() => ({
  ready: entry.value.status === "ready",
  error: entry.value.error,
  value: { entry: entry.value, range: historyRange.value }
}));

const chart = computed(() => {
  const shown = history.value.value;
  if (!shown) return null;
  const { points, resolution } = shown.entry;
  const axis = bucketAxis(
    points.map((point) => point.timestamp),
    resolution
  );
  const series: ChartSeries[] =
    view.value === "total"
      ? [
          {
            id: "total",
            name: "总耗时",
            color: "var(--series-1)",
            values: fillAxis(axis, points, (point) => point.total_ms)
          }
        ]
      : PHASES.map((phase) => ({
          id: phase.key,
          name: phase.label,
          color: phase.color,
          // A stage that does not apply (no TLS on plain HTTP) stacks as nothing, not as a gap.
          values: fillAxis(axis, points, (point) => point[phase.key] ?? (point.total_ms == null ? null : 0))
        }));
  return {
    axis,
    series,
    // An API without bucketing sends one probe per point and no counts.
    failed: fillAxis(axis, points, (point) => point.failures ?? 1 - point.success),
    samples: fillAxis(axis, points, (point) => point.samples ?? 1),
    label: RANGE_LABELS[shown.range]
  };
});

/** Figures for the range on screen; the 24-hour baseline stands in when the API sends none. */
const stats = computed(() => {
  const shown = history.value.value;
  const baseline = props.endpoint.baseline;
  if (shown?.entry.stats) {
    const { median_ms, p95_ms, availability, failures } = shown.entry.stats;
    return { label: RANGE_LABELS[shown.range], median: median_ms, p95: p95_ms, availability, failures };
  }
  return {
    label: RANGE_LABELS["24h"],
    median: baseline?.median_ms ?? null,
    p95: baseline?.p95_ms ?? null,
    availability: baseline?.availability ?? null,
    failures: baseline?.failures ?? 0
  };
});

const ok = computed(() => props.endpoint.success === 1);
const host = computed(() => {
  try {
    return new URL(props.endpoint.url).hostname;
  } catch {
    return props.endpoint.url;
  }
});

const phases = computed(() => PHASES.map((phase) => ({ ...phase, value: props.endpoint[phase.key] })));
const timed = computed(() => phases.value.filter((phase) => phase.value != null));
const failures = computed(() => Math.round(stats.value.failures));
</script>

<template>
  <div class="detail">
    <p v-if="!ok && endpoint.success != null" class="detail__error" role="status">
      <span class="dot dot--bad" aria-hidden="true"></span>
      最近一次探测失败：{{ endpoint.error || "没有收到响应" }}
    </p>

    <dl class="detail__stats">
      <div>
        <dt>当前</dt>
        <dd>
          <template v-if="ok">{{ formatGrouped(endpoint.total_ms) }}<span class="unit">ms</span></template>
          <template v-else>{{ endpoint.success == null ? MISSING : "不可达" }}</template>
        </dd>
      </div>
      <div>
        <dt>{{ stats.label }}中位</dt>
        <dd>{{ formatGrouped(stats.median) }}<span v-if="stats.median != null" class="unit">ms</span></dd>
      </div>
      <div>
        <dt title="95% 的探测比这个值快">P95</dt>
        <dd>{{ formatGrouped(stats.p95) }}<span v-if="stats.p95 != null" class="unit">ms</span></dd>
      </div>
      <div>
        <dt>可用率</dt>
        <dd>
          {{ formatAvailability(stats.availability) }}
          <span v-if="failures > 0" class="unit">失败 {{ formatGrouped(failures) }} 次</span>
        </dd>
      </div>
    </dl>

    <div class="detail__phases">
      <div
        v-if="ok && timed.length"
        class="phase-bar"
        role="img"
        :aria-label="`最近一次请求各阶段耗时：${timed.map((phase) => `${phase.label} ${formatMs(phase.value)} 毫秒`).join('，')}`"
      >
        <i
          v-for="phase in timed"
          :key="phase.key"
          :style="{ flexGrow: Math.max(phase.value ?? 0, 0.01), background: phase.color }"
        ></i>
      </div>
      <ul class="phase-legend">
        <li v-for="phase in phases" :key="phase.key">
          <i :style="{ background: phase.color }" aria-hidden="true"></i>
          <span class="phase-legend__name">{{ phase.label }}</span>
          <strong class="num">{{ formatMs(phase.value) }}<span v-if="phase.value != null" class="unit">ms</span></strong>
          <span class="phase-legend__hint">{{ phase.hint }}</span>
        </li>
      </ul>
    </div>

    <div class="detail__chart-head">
      <div class="segmented" role="group" aria-label="图表内容">
        <button type="button" :aria-pressed="view === 'total'" @click="view = 'total'">总耗时</button>
        <button type="button" :aria-pressed="view === 'phases'" @click="view = 'phases'">阶段构成</button>
      </div>
      <RangeTabs />
    </div>

    <div v-if="history.error && !chart" class="detail__state" role="alert">
      <p>{{ history.error }}</p>
      <button class="text-link" type="button" @click="retryEndpointHistory(endpoint.id, historyRange)">
        重新读取
      </button>
    </div>
    <div v-else-if="!chart" class="detail__state" aria-live="polite">正在读取走势…</div>
    <TimeChart
      v-else
      :times="chart.axis.times"
      :step-ms="chart.axis.stepMs"
      :series="chart.series"
      :stacked="view === 'phases'"
      :failed="chart.failed"
      :samples="chart.samples"
      unit="ms"
      :busy="history.pending"
      :label="`${endpoint.name} ${chart.label}延迟走势`"
    />

    <p class="detail__meta">
      <span v-if="endpoint.http_code">HTTP {{ endpoint.http_code }}</span>
      <span v-if="endpoint.remote_ip" class="num">{{ endpoint.remote_ip }}</span>
      <a class="text-link" :href="endpoint.url" target="_blank" rel="noreferrer">
        {{ host }}<AppIcon name="arrow-up-right" :size="13" />
      </a>
    </p>
  </div>
</template>

<style scoped>
.detail { display: grid; gap: 1.5rem; padding: 0.5rem 0 1.75rem 1.5rem; }

.detail__error {
  display: flex;
  gap: 0.6rem;
  align-items: baseline;
  color: var(--ink);
  font-size: 0.875rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.detail__error .dot { transform: translateY(-0.05rem); }

.detail__stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 1rem; }
.detail__stats dt { color: var(--ink-3); font-size: 0.8125rem; }
.detail__stats dd { margin-top: 0.15rem; font-size: 1.375rem; font-weight: 500; letter-spacing: -0.01em; }

.detail__phases { display: grid; gap: 0.75rem; }
/* Adjacent fills are separated by a gap in the page colour, never by an outline. */
.phase-bar { display: flex; gap: 2px; height: 0.5rem; }
.phase-bar i { min-width: 3px; border-radius: 1px; }
.phase-bar i:first-child { border-radius: 4px 1px 1px 4px; }
.phase-bar i:last-child { border-radius: 1px 4px 4px 1px; }
.phase-legend {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem 1rem;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 0.8125rem;
}
.phase-legend li { display: grid; grid-template-columns: auto minmax(0, 1fr); column-gap: 0.45rem; align-items: center; }
.phase-legend i { width: 0.5rem; height: 0.5rem; border-radius: 2px; }
.phase-legend__name { color: var(--ink-2); }
.phase-legend strong { grid-column: 2; font-size: 0.9375rem; font-weight: 500; }
.phase-legend__hint { grid-column: 2; color: var(--ink-3); font-size: 0.75rem; }

.detail__chart-head {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: -0.5rem;
}
.detail__state {
  display: grid;
  gap: 0.5rem;
  place-content: center;
  justify-items: center;
  height: 220px;
  color: var(--ink-3);
  font-size: 0.875rem;
}

.detail__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1.25rem;
  align-items: center;
  color: var(--ink-3);
  font-size: 0.8125rem;
}

@media (max-width: 640px) {
  .detail { gap: 1.25rem; padding-left: 0; }
  .detail__stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .detail__stats dd { font-size: 1.25rem; }
  .phase-legend { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
